<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { EcxProgressBar } from '@ecx/ui'
import { useBulkImportStore } from '@/stores/bulkImport'
import type { BulkImportEntity } from '@/types/bulkImport'
import processingFolderUsers from '@/assets/bulkImport/processing-folder.svg'
import processingFolderVehicles from '@/assets/bulkImport/processing-folder-vehicles.svg'
import processingFolderCards from '@/assets/bulkImport/processing-folder-cards.png'

const PROCESSING_ILLUSTRATIONS: Record<BulkImportEntity, string> = {
  users: processingFolderUsers,
  vehicles: processingFolderVehicles,
  cards: processingFolderCards,
}

const { t } = useI18n()
const store = useBulkImportStore()

const processed = computed(() => store.currentImport?.progress?.processed_count ?? 0)
const total = computed(() => store.currentImport?.progress?.total_count ?? 0)
const percent = computed(() => store.currentImport?.progress?.percent ?? 0)
const isVehicles = computed(() => store.entity === 'vehicles')
const isEdit = computed(() => store.mode === 'edit')
const illustrationSrc = computed(() => PROCESSING_ILLUSTRATIONS[store.entity])

const titleText = computed(() => {
  if (isEdit.value) return t('corp.bulk_import.edit.processing.title')
  return isVehicles.value
    ? t('corp.bulk_import.processing.title_vehicles')
    : t('corp.bulk_import.processing.title')
})

const descriptionText = computed(() =>
  isEdit.value
    ? t('corp.bulk_import.edit.processing.description')
    : t('corp.bulk_import.processing.description'),
)
</script>

<template>
  <div class="flex flex-col items-center justify-center text-center gap-4 flex-1 p-6">
    <img
      :src="illustrationSrc"
      alt=""
      class="w-24"
    />

    <div
      class="w-full max-w-sm flex flex-col gap-2"
      role="status"
      aria-live="polite"
      :aria-busy="percent < 100"
    >
      <div class="flex items-center justify-between text-sm">
        <span>
          <span class="text-grey-950 font-medium">{{ processed }}</span
          ><span class="text-grey-400">/{{ total }}</span>
        </span>
        <span class="text-grey-950 font-medium" data-testid="percent-label">{{ percent }}%</span>
      </div>
      <EcxProgressBar
        :current="processed"
        :total="total"
        variant="royal-gradient"
        rounded="sm"
      />
      <span
        class="sr-only"
        role="progressbar"
        :aria-valuenow="percent"
        aria-valuemin="0"
        aria-valuemax="100"
      ></span>
    </div>

    <h2 class="text-2xl font-semibold text-grey-950">
      {{ titleText }}
    </h2>

    <p class="text-sm text-grey-600">
      {{ descriptionText }}
    </p>
  </div>
</template>
